import React, { useEffect, useRef, useState } from 'react';
import { FormGroup, FormText } from 'reactstrap';
import { reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import Immutable from 'immutable';
import FormItem from '../../../../common/forms/FormItem';
import Table from '../../../../common/Table';
import { generateManifestCargoItemsColumns } from '../../../../../config/columns';
import { getCargoItemByCorrelation } from '../../../../../actions';
import Badge from '../../../../common/Badge';
import { filterMethodCaseInsensitive } from '../../../../../utils/filter-methods';
import { CARGO_OPERATIONS } from '../../../../../config/constants';

const CargoOperationCorrelativeForm = ({
  manifestItems,
  items,
  setItems,
  dispatchGetCargoItemByCorrelation,
  cargoOperation,
  itineraryId,
  locationId,
}) => {
  const [correlationInput, setCorrelationInput] = useState('');

  const focusInput = () => {
    const input = document.querySelector('.qr-correlation');
    if (input) {
      input.focus();
    }
  };

  useEffect(() => {
    focusInput();
  }, []);

  const [pendingCorrelations, setPendingCorrelations] = useState([]);

  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const processingRef = React.useRef(new Set());

  const onSubmit = () => {
    const value = correlationInput.trim();
    if (!value) return;

    setCorrelationInput('');
    focusInput();

    if (processingRef.current.has(value)) {
      return;
    }

    const currentItems = itemsRef.current;

    if (currentItems.find((item) => item.correlation === value)) {
      toastr.warning('Advertencia', 'El item ya fue agregado', {
        disableCloseButtonFocus: true,
      });
      return;
    }

    const manifestItemsToAdd = manifestItems.find(
      (manifestItem) => manifestItem.correlation === value,
    );

    if (manifestItemsToAdd) {
      setItems((prev) => [...prev, manifestItemsToAdd]);
      return;
    }

    if (cargoOperation.value !== CARGO_OPERATIONS.ADD_ITEM.value) {
      toastr.warning(
        'Advertencia',
        'El item no se encuentra en el manifiesto',
        {
          disableCloseButtonFocus: true,
        },
      );
      return;
    }

    processingRef.current.add(value);
    setPendingCorrelations((prev) => [...prev, value]);

    dispatchGetCargoItemByCorrelation({
      correlation: value,
      searchType: cargoOperation.searchType,
      itineraryId,
      locationId,
    })
      .then((response) => {
        if (!response) return;
        const { correlation: itemCorrelation, weight, description } = response;
        setItems((prev) => {
          if (prev.find((item) => item.correlation === itemCorrelation)) {
            return prev;
          }
          return [
            ...prev,
            {
              correlation: itemCorrelation,
              weight,
              description,
              isSearched: true,
              ...response,
            },
          ];
        });
      })
      .finally(() => {
        processingRef.current.delete(value);
        setPendingCorrelations((prev) => prev.filter((c) => c !== value));
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onSubmit();
    }
  };

  const onClickRemoveItem = ({ correlation }) => {
    const removeItem = items.filter((item) => item.correlation !== correlation);
    setItems(removeItem);
  };

  return (
    <div>
      <FormGroup row>
        <FormItem label="Correlativo" required>
          <input
            className="form-control qr-correlation"
            type="text"
            placeholder="Correlativo"
            value={correlationInput}
            onChange={(e) => setCorrelationInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <FormText>
            ¡Mantener el foco en el campo de texto mientras se escanean los
            códigos QR!
          </FormText>
        </FormItem>
      </FormGroup>
      {pendingCorrelations.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Buscando:
          </span>
          {pendingCorrelations.map((c) => (
            <Badge key={c} text={`● ${c}`} color="info" />
          ))}
        </div>
      )}
      <Table
        columns={generateManifestCargoItemsColumns(onClickRemoveItem)}
        data={items}
        manual={false}
        filterable
        filterMethod={filterMethodCaseInsensitive}
      />
    </div>
  );
};

CargoOperationCorrelativeForm.propTypes = {
  manifestItems: PropTypes.instanceOf(Immutable.List).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      correlation: PropTypes.string,
      description: PropTypes.string,
      weight: PropTypes.number,
    }),
  ).isRequired,
  setItems: PropTypes.func.isRequired,
  dispatchGetCargoItemByCorrelation: PropTypes.func.isRequired,
  cargoOperation: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    labelDinamic: PropTypes.func.isRequired,
    searchType: PropTypes.string.isRequired,
  }).isRequired,
  itineraryId: PropTypes.number.isRequired,
  locationId: PropTypes.number.isRequired,
};

const formComponent = reduxForm({
  form: 'CargoOperationCorrelativeForm',
})(CargoOperationCorrelativeForm);

const mapStateToProps = ({ CargoUnit }) => ({
  manifestItems: CargoUnit.ManifestCargo.getIn(['all', 'content']).map(
    (option) => ({
      correlation: option.correlation,
      description: option.description,
      weight: option.weight,
    }),
  ),
});

const mapDispatchToProps = {
  dispatchGetCargoItemByCorrelation: getCargoItemByCorrelation,
};

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
